import { ChevronRightIcon, CubeIcon, DoubleArrowRightIcon, MixIcon } from '@radix-ui/react-icons';
import { Badge, Box, Card, FeatureIcon, Flex, Heading, PageNav, Progress, Separator, Text } from '@workos-inc/design-system';
import { getMatter, mdxPlugins, pathToSlug, rehypeQuickNav, remarkMatchStack, remarkScreenshots, slugToPath } from '@workos-inc/docs-kit';
import fs from 'fs';
import { glob } from 'glob';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { type MDXRemoteProps, MDXRemote } from 'next-mdx-remote';
import { serialize } from 'next-mdx-remote/serialize';
import path from 'path';
import * as React from 'react';
import { z } from 'zod';
import { AuthKitStackSelector } from 'src/components/authkit-stack-selector';
import { Breadcrumb } from 'src/components/breadcrumb';
import { CopyMarkdownButton } from 'src/components/copy-markdown-button';
import { Header } from 'src/components/header';
import { mdxComponentsBySlug } from 'src/components/mdx-components';
import { mdxRemoteScope } from 'src/components/mdx-scope';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { ReferenceDataProvider } from 'src/components/reference-data-provider';
import { Subtitle } from 'src/components/subtitle';
import { ApiReferenceIcon } from 'src/icons/api-reference-icon';
import { EventsIcon } from 'src/icons/events-icon';
import { useAuth } from 'src/utils/auth-context';
import { CONTENT_DIR, NAVIGATION_FILENAME } from 'src/utils/constants';
import { createPageDescription, createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
import { NavMeta, NavMetaLink } from 'src/utils/nav-meta';
import { getReferenceMeta } from 'src/utils/reference-meta';
import { ReferenceData, ReferenceLanguage } from 'src/utils/reference-schema';
import { rehypeCodeBlockHoverCards } from 'src/utils/rehype-code-block-hover-cards';
import { rehypeCodeBlockReplacements } from 'src/utils/rehype-code-block-replacements';
import { rehypeCodeBlockSnippets } from 'src/utils/rehype-code-block-snippets';
import { getBackendStack, getFrontendStack, getStackSlugs } from 'src/utils/stack-selection';
import { featureFlag, useIsFeatureFlagEnabled } from 'src/utils/use-feature-flags';
type PageFrontMatter = z.infer<typeof PageFrontMatter>;
const PageFrontMatter = z.object({
  title: z.string(),
  description: z.string(),
  featureFlag: featureFlag.optional(),
  badge: z.string().optional(),
  icon: z.string().optional(),
  breadcrumb: z.object({
    title: z.string(),
    url: z.string()
  }).optional(),
  showNextPage: z.boolean().optional(),
  hideCopyButton: z.boolean().optional()
}).strict();
interface NextPath {
  params: {
    slug: string[];
  };
}

/** Whether the page shouldn't be handled by this route */
const isExcluded = (slug: string[]) => {
  const excludedSlugs = ['reference', 'sdks'];
  const firstSegment = slug[0];
  const lastSegment = slug[slug.length - 1];
  const isNavMeta = lastSegment === NAVIGATION_FILENAME.replace('.mdx', '');
  return firstSegment && excludedSlugs.includes(firstSegment) || isNavMeta;
};
export const getStaticPaths: GetStaticPaths = async () => {
  // Grab all mdx files and build an array of their paths
  const paths: NextPath[] = await glob(`${CONTENT_DIR}/**/**.mdx`).then(matches => matches.map(pathname => ({
    params: {
      slug: pathToSlug(pathname)
    }
  })).filter(pathname => !isExcluded(pathname.params.slug)));

  // Since user-management sub pages are "dynamic" (no mdx files for each),
  // we need to add all the stack combinations to the paths array
  const userManagementPaths = getStackSlugs(['user-management']);

  // Add /authkit explicitly since it maps to landing.mdx (not index.mdx)
  const authKitLandingPath = {
    params: {
      slug: ['authkit']
    }
  };
  return {
    paths: [...paths, ...userManagementPaths, authKitLandingPath],
    fallback: 'blocking'
  };
};
export const getStaticProps: GetStaticProps = async context => {
  if (context.params === undefined) {
    return {
      notFound: true
    };
  }

  // Make slug an array if it's not
  const slug = context.params.slug ? [context.params.slug].flat() : [];
  if (isExcluded(slug)) {
    return {
      notFound: true
    };
  }

  // Reconstruct the path to the MDX file
  // Special case: /authkit maps to landing.mdx instead of index.mdx
  const isAuthKitLanding = slug.length === 1 && slug[0] === 'authkit';
  const [defaultFilePath, matchingSlug] = slugToPath(slug);
  const filePath = isAuthKitLanding ? path.join(process.cwd(), CONTENT_DIR, 'authkit', 'landing.mdx') : defaultFilePath;
  const asSlug = [...matchingSlug];
  let validFrontendStack: string | null = null;
  let validBackendStack: string | null = null;

  // For user management, we try parsing the path parts as frontend and backend stacks
  // We then pass this values to the `remarkMatchStack` remark plugin, where it will
  // keep only the content related to this page.
  if (asSlug.at(0) === 'authkit') {
    validFrontendStack = getFrontendStack(slug.at(1), null);
    validBackendStack = getBackendStack(slug.at(2), null);
    if (validFrontendStack) {
      asSlug.push(validFrontendStack);
    }
    if (validFrontendStack && validBackendStack) {
      asSlug.push(validBackendStack);
    }
  }
  const url = '/' + asSlug.join('/');
  const navUrl = '/' + matchingSlug.join('/');
  if (filePath === null) {
    return {
      notFound: true
    };
  }
  const referenceMeta = await getReferenceMeta({
    skipDescriptions: true
  });
  const usedReferenceIds: string[] = [];
  const rawMdx = await new Promise<Buffer>(resolve => fs.readFile(filePath, (_, data) => resolve(data)));
  const {
    compiledSource,
    frontmatter: fm
  } = await serialize(rawMdx, {
    parseFrontmatter: true,
    mdxOptions: {
      remarkPlugins: [remarkMatchStack({
        frontend: validFrontendStack,
        backend: validBackendStack
      }), ...mdxPlugins.remark, remarkScreenshots],
      rehypePlugins: [rehypeQuickNav(asSlug), rehypeCodeBlockSnippets(filePath), ...mdxPlugins.rehype, rehypeCodeBlockHoverCards(referenceMeta.reference, usedReferenceIds), rehypeCodeBlockReplacements]
    }
  });
  let frontmatter: PageFrontMatter;
  try {
    frontmatter = PageFrontMatter.parse(fm);
  } catch (error) {
    throw new Error(`Invalid page metadata in ${filePath}\n${String(error)}`);
  }

  // Given "/foo/bar/baz/qux" slug, look for a file
  // with navigation metadata in "/foo" folder
  const navigationPathname = path.join(process.cwd(), CONTENT_DIR, slug[0] ?? '', NAVIGATION_FILENAME);
  const defaultNavMeta: NavMeta = {
    title: '',
    links: []
  };
  const actualNavMeta = getMatter(navigationPathname);
  const navMeta = Object.assign(defaultNavMeta, actualNavMeta);
  const referenceData: ReferenceData = {};

  // Builds referenceData with only entities that are used in this page, saving bundle size
  for (const [language, entities] of Object.entries(referenceMeta.reference)) {
    const usedElements = entities.filter(entity => entity.id && usedReferenceIds.includes(entity.id));
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    referenceData[language as ReferenceLanguage] = usedElements;
  }
  let nextPageMeta: PageMeta | null = null;
  const nextPageUrl = getNextUrl(navUrl, navMeta.links);
  if (nextPageUrl) {
    const nextPageSlug = nextPageUrl.split('/').filter(Boolean);
    const [nextPageFilePath] = slugToPath(nextPageSlug);
    // Introducing lint rule banning type assertions
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const data = getMatter(nextPageFilePath ?? '') as PageMeta;
    nextPageMeta = data.title ? data : null;
  }
  const image = await createPageImage(frontmatter.title, frontmatter.description, navMeta.title);

  // A page is non-canonical when it includes stack-specific segments
  // (e.g., /authkit/nextjs/node). These should be excluded from Algolia
  // indexing to avoid duplicate results.
  const isNonCanonical = validFrontendStack !== null;
  return {
    props: {
      asSlug: url,
      navSlug: navUrl,
      content: compiledSource,
      navMeta,
      pageMeta: {
        ...frontmatter,
        image,
        canonicalUrl: `https://workos.com/docs${url}`
      },
      nextPageMeta,
      referenceData,
      isAuthKitLanding,
      isNonCanonical
    }
  };
};
type FeaturePage = string | {
  path: string;
  referenceSlug?: string | null;
};
const featurePages: FeaturePage[] = ['/sso', '/directory-sync', '/mfa', '/magic-link', '/admin-portal', '/audit-logs', '/authkit', '/domain-verification', '/fga', '/vault', '/feature-flags'];
const getFeaturePageConfig = (slug: string): FeaturePage | undefined => {
  return featurePages.find(page => typeof page === 'string' ? page === slug : page.path === slug);
};
const getReferenceSlug = (slug: string): string | null => {
  const config = getFeaturePageConfig(slug);
  if (!config) {
    return null;
  }
  if (typeof config === 'string') {
    return config;
  }
  return config.referenceSlug !== null ? config.referenceSlug ?? config.path : null;
};
interface PageMeta extends PageFrontMatter {
  canonicalUrl: string;
  image: string;
}
interface PageProps {
  asSlug: string;
  navSlug: string;
  content: string;
  navMeta: NavMeta;
  pageMeta: PageMeta;
  nextPageMeta: PageMeta | null;
  referenceData: ReferenceData;
  isAuthKitLanding?: boolean;
  isNonCanonical: boolean;
}
const Page = ({
  asSlug,
  navSlug,
  content,
  navMeta,
  pageMeta,
  nextPageMeta,
  referenceData,
  isAuthKitLanding,
  isNonCanonical
}: PageProps) => {
  const router = useRouter();
  const {
    isLoading: userLoading,
    user
  } = useAuth();
  const isAllowed = useIsFeatureFlagEnabled(pageMeta?.featureFlag);
  const isForbidden = user && !userLoading && !isAllowed;
  const isUnauthorized = !user && !userLoading && !isAllowed;
  const isLoading = router.isFallback || !isAllowed;
  if (isUnauthorized) {
    void router.replace(`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/signin?redirect=${window.location.href}`);
  }
  if (isForbidden) {
    void router.replace('/404');
  }
  if (isLoading) {
    return <Flex align="center" justify="center" style={{
      height: '100vh',
      paddingBottom: '5vh'
    }}>
        <Head>
          <title>{createPageTitle()}</title>
          <meta content="noindex" name="robots" />
        </Head>
        <Progress />
      </Flex>;
  }
  const title = createPageTitle(pageMeta.title, navMeta.title);
  const description = createPageDescription(pageMeta.description);
  const {
    image
  } = pageMeta;
  type FeatureIconProps = React.ComponentProps<typeof FeatureIcon>;
  // Introducing lint rule banning type assertions
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const icon = asSlug.substring(1) as FeatureIconProps['feature'];
  const isFeaturePage = getFeaturePageConfig(asSlug) !== undefined && !isAuthKitLanding;
  const referenceSlug = getReferenceSlug(asSlug);
  const mdxComponentsSlug = asSlug === '/events' ? 'events' : '*';
  const mdxComponents = mdxComponentsBySlug[mdxComponentsSlug];
  return <Box data-algolia-exclude={isNonCanonical ? true : undefined} data-sentry-element="Box" data-sentry-component="Page" data-sentry-source-file="[...slug].tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="[...slug].tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="[...slug].tsx" />

        {asSlug.startsWith('/demo') && <meta content="noindex" name="robots" />}

        <link href={pageMeta.canonicalUrl} rel="canonical" />
      </Head>
      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="[...slug].tsx" />
      <Header caption={navMeta.title || 'Docs'} data-sentry-element="Header" data-sentry-source-file="[...slug].tsx" />
      <PageLayout.Root layout={navMeta.links.length ? 'sidebar' : 'centered'} data-sentry-element="PageLayout.Root" data-sentry-source-file="[...slug].tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="[...slug].tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="[...slug].tsx">
            {navMeta.links.length > 0 && <PageNav.Root color="purple">
                {navMeta.links?.map(link => <NavRecursive key={link.title} {...link} slug={navSlug} />)}

                <Box display={{
              initial: 'none',
              md: 'block'
            }}>
                  <Separator my="4" size="2" />

                  <PageNav.Link asChild>
                    <NextLink href={`/reference${referenceSlug ?? ''}`}>
                      <PageNav.LinkIcon>
                        <ApiReferenceIcon />
                      </PageNav.LinkIcon>
                      API Reference
                    </NextLink>
                  </PageNav.Link>

                  {!asSlug.startsWith('/events') && <PageNav.Link asChild>
                      <NextLink href="/events">
                        <PageNav.LinkIcon>
                          <EventsIcon />
                        </PageNav.LinkIcon>
                        Events
                      </NextLink>
                    </PageNav.Link>}

                  <PageNav.Link asChild>
                    <NextLink href="/integrations">
                      <PageNav.LinkIcon>
                        <MixIcon />
                      </PageNav.LinkIcon>
                      Integrations
                    </NextLink>
                  </PageNav.Link>

                  <PageNav.Link asChild>
                    <NextLink href="/migrate">
                      <PageNav.LinkIcon>
                        <DoubleArrowRightIcon />
                      </PageNav.LinkIcon>
                      Migrate to WorkOS
                    </NextLink>
                  </PageNav.Link>

                  <PageNav.Link asChild>
                    <NextLink href="/sdks">
                      <PageNav.LinkIcon>
                        <CubeIcon />
                      </PageNav.LinkIcon>
                      SDKs
                    </NextLink>
                  </PageNav.Link>
                </Box>
              </PageNav.Root>}
          </NavigationWrapper>
        </PageLayout.Nav>

        <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="[...slug].tsx">
          {pageMeta.breadcrumb && <Breadcrumb href={pageMeta.breadcrumb.url}>
              {pageMeta.breadcrumb.title}
            </Breadcrumb>}

          <Flex data-section-slug={asSlug} direction={{
          initial: 'column',
          sm: 'row-reverse'
        }} gap="5" mb="6" data-sentry-element="Flex" data-sentry-source-file="[...slug].tsx">
            {isFeaturePage && <FeatureIcon feature={icon} mt={{
            sm: '2'
          }} size="3" />}
            <Box flexGrow="1" data-sentry-element="Box" data-sentry-source-file="[...slug].tsx">
              <Flex align="center" gap="3" justify="between" mb="-4" tabIndex={-1} data-sentry-element="Flex" data-sentry-source-file="[...slug].tsx">
                <Flex align="center" gap="3" data-sentry-element="Flex" data-sentry-source-file="[...slug].tsx">
                  <Heading as="h1" size="7" data-sentry-element="Heading" data-sentry-source-file="[...slug].tsx">
                    {pageMeta.title}
                  </Heading>
                  {pageMeta.badge && <Badge color="purple" size="3">
                      {pageMeta.badge}
                    </Badge>}
                </Flex>
                {asSlug.startsWith('/authkit/') && <AuthKitStackSelector currentSlug={asSlug} />}
              </Flex>

              {pageMeta.description && <Subtitle mt="5">{pageMeta.description}</Subtitle>}
            </Box>
          </Flex>
          {!pageMeta.hideCopyButton && <CopyMarkdownButton slug={asSlug.split('/').filter(Boolean)} />}

          {isFeaturePage && <Separator my="7" size="4" />}

          <ReferenceDataProvider value={referenceData} data-sentry-element="ReferenceDataProvider" data-sentry-source-file="[...slug].tsx">
            <MDXRemote compiledSource={content}
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          components={mdxComponents as MDXRemoteProps['components']} frontmatter={mdxRemoteFrontmatter} scope={mdxRemoteScope} data-sentry-element="MDXRemote" data-sentry-source-file="[...slug].tsx" />
          </ReferenceDataProvider>

          {nextPageMeta && pageMeta.showNextPage && <Card asChild my="6" size={{
          initial: '2',
          xs: '3'
        }} variant="classic">
              <NextLink href={getNextUrl(navSlug, navMeta.links) ?? ''}>
                <Flex align="stretch" gap={{
              initial: '3',
              xs: '5'
            }} justify="between">
                  <Flex direction="column">
                    <Text size="3" trim="start" weight="bold">
                      {nextPageMeta.title}
                    </Text>
                    <Text color="gray" size="2" trim="end">
                      {nextPageMeta.description.replace(/\.$/, '')}
                    </Text>
                  </Flex>

                  <Flex align="center" display={{
                xs: 'none'
              }} mx="-1">
                    <ChevronRightIcon color="var(--gray-a9)" height="20" width="20" />
                  </Flex>

                  <Flex align="center" display={{
                initial: 'none',
                xs: 'flex'
              }} flexShrink="0" gap="5">
                    <Separator orientation="vertical" size="2" />
                    <Flex align="center" gap="1">
                      <Text as="div" color="gray" size="2" style={{
                    marginTop: -2
                  }}>
                        Up next
                      </Text>
                      <ChevronRightIcon color="var(--gray-a9)" height="16" width="16" />
                    </Flex>
                  </Flex>
                </Flex>
              </NextLink>
            </Card>}
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};

// Don't pass empty object to MDX Remote directly.
// MDX Remote must receive a stable object, otherwise performance on large pages suffers.
const mdxRemoteFrontmatter = {};

/** Get the next URL after current based on the navigation metadata */
const getNextUrl = (currentUrl: string, links: NavMetaLink[]) => {
  const getLinksRecursive = (link: NavMetaLink): NavMetaLink[] => {
    const {
      title,
      url,
      links
    } = link;
    const nestedLinks = links?.map(getLinksRecursive).flat() ?? [];
    return url ? [{
      title,
      url
    }, ...nestedLinks] : nestedLinks;
  };
  const navMetaFlat = links.map(getLinksRecursive).flat();
  const pageIndex = navMetaFlat.findIndex(link => link.url === currentUrl);
  return navMetaFlat[pageIndex + 1]?.url;
};
const NavRecursive = ({
  url,
  title,
  links,
  slug,
  featureFlag
}: NavMetaLink & {
  slug: string;
}) => {
  const state = slug === url ? 'active' : 'normal';
  const isAllowed = useIsFeatureFlagEnabled(featureFlag);
  if (!isAllowed) {
    return null;
  }
  if (url === undefined) {
    return <>
        <PageNav.Label>{title}</PageNav.Label>
        {links?.map(link => <NavRecursive key={link.title} {...link} slug={slug} />)}
      </>;
  }
  if (links) {
    const subOpen = state === 'active';
    return <PageNav.Sub key={url} open={subOpen}>
        <PageNav.Link asChild state={state}>
          <NextLink href={url}>{title}</NextLink>
        </PageNav.Link>
        <PageNav.SubContent>
          {links.map(link => <NavRecursive key={link.title} {...link} slug={slug} />)}
        </PageNav.SubContent>
      </PageNav.Sub>;
  }
  return <PageNav.Link asChild state={state} data-sentry-element="PageNav.Link" data-sentry-component="NavRecursive" data-sentry-source-file="[...slug].tsx">
      <NextLink href={url} data-sentry-element="NextLink" data-sentry-source-file="[...slug].tsx">{title}</NextLink>
    </PageNav.Link>;
};
export default Page;